import { ArrowBackOutlined } from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  IconButton,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Button from "../../../components/common/Button";
import apiService from "../../../service/Api";
import { tokens } from "../../../theme";
import type { IEquipment } from "../../../types/equipment";
import GearHeader from "./GearHeader";
import AllocationsTab from "./tabs/AllocationsTab";
import ComponentsTab from "./tabs/ComponentsTab";
import DocumentsTab from "./tabs/DocumentsTab";
import MaintenanceTab from "./tabs/MaintenanceTab";
import OverviewTab from "./tabs/OverviewTab";
import RentalsTab from "./tabs/RentalsTab";
import ImagesTab from "./tabs/ImagesTab";
import AddEquipmentDialog from "./AddEquipmentDialog";
import { FormDataType } from "../types/types";

type EquipmentApiResponse = {
  id: number;
  images: Array<{ image?: string; url?: string }>;
  allocations: unknown[];
  serial_number: string;
  name: string;
  brand: string;
  category: string;
  sub_category: string;
  status: string;
  condition: string;
  location: string | null;
  purchase_date: string | null;
  last_maintenance_date: string | null;
  next_maintenance_date: string | null;

  hours_since_last_service?: number | string;

  last_service_date?: string | null;
  next_maintenance?: string | null;
  last_used?: string | null;
  assignee?: string | null;
  quantity?: string | null;
  supplier?: string | null;
  is_kit?: boolean;

  asset_tag?: string;
  model?: string;
  manufacture?: string;
  length?: string;
  height?: string;
  width?: string;
  weight?: string;
  transport_volume?: string;
  measuring_unit?: string;
  current?: string;
  power?: string;
  purchase_cost?: string;
  current_value?: string;
  warranty_expiry?: string;
  rental_value?: string;
  supplier_name?: string;
  contact_name?: string;
  supplier_phone?: string;
  supplier_email?: string;
  supplier_website?: string;
};

export default function ViewEquipmentPage() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const { id } = useParams();
  const navigate = useNavigate();

  const [equipment, setEquipment] = useState<IEquipment | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [editOpen, setEditOpen] = useState(false);

  const fetchEquipment = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await apiService.get<EquipmentApiResponse>(
        `/api/equipments/equipment/${id}/`,
      );

      const raw = response.data;

      const equipmentData: IEquipment = {
        id: String(raw.id),
        images: raw.images || [],
        serialNo: raw.serial_number || "",
        name: raw.name || "",
        brand: raw.brand || "",
        category: raw.category || "",
        subCategory: raw.sub_category || "",
        purchaseDate: raw.purchase_date || "",
        status: raw.status || "",
        lastServiceDate:
          raw.last_service_date || raw.last_maintenance_date || undefined,
        nextMaintenance:
          raw.next_maintenance || raw.next_maintenance_date || undefined,
        hoursSinceLastService:
          typeof raw.hours_since_last_service === "string"
            ? Number(raw.hours_since_last_service)
            : raw.hours_since_last_service,
        assignee: raw.assignee || undefined,
        quantity: raw.quantity || "",
        location: raw.location || "",
        supplier: raw.supplier || "",
        lastUsed: raw.last_used || "",
        condition: raw.condition || "",
        isKit: raw.is_kit || false,
        assetTag: raw.asset_tag || undefined,
        model: raw.model || undefined,
        manufacture: raw.manufacture || undefined,
        length: raw.length || undefined,
        height: raw.height || undefined,
        width: raw.width || undefined,
        weight: raw.weight || undefined,
        transportVolume: raw.transport_volume || undefined,
        measuringUnit: raw.measuring_unit || undefined,
        current: raw.current || undefined,
        power: raw.power || undefined,
        purchaseCost: raw.purchase_cost || undefined,
        currentValue: raw.current_value || undefined,
        warrantyExpiry: raw.warranty_expiry || undefined,
        rentalValue: raw.rental_value || undefined,
        supplierName: raw.supplier_name || undefined,
        contactName: raw.contact_name || undefined,
        supplierPhone: raw.supplier_phone || undefined,
        supplierEmail: raw.supplier_email || undefined,
        supplierWebsite: raw.supplier_website || undefined,
      };

      setEquipment(equipmentData);
    } catch (error) {
      console.error("Failed to fetch gear", error);
      setEquipment(null);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) fetchEquipment();
  }, [id, fetchEquipment]);

  const handleUpdateEquipment = async (data: FormDataType) => {
    if (!equipment) return;

    try {
      const payload = {
        serial_number: data.serialNo,
        name: data.name,
        brand: data.brand,
        category: data.category,
        sub_category: data.subCategory,
        location: data.location,
        status: data.status,

        ...(data.purchaseDate && {
          purchase_date: data.purchaseDate,
        }),
      };

      await apiService.put(
        `/api/equipments/equipment/${equipment.id}/`,
        payload,
      );

      setEditOpen(false);
      await fetchEquipment();
    } catch (err) {
      console.error("Failed to update equipment", err);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress sx={{ color: colors.primary[400] }} />
      </Box>
    );
  }

  const handleImageSearch = () => {
    const imageUrl =
      equipment?.images?.[0]?.image || equipment?.images?.[0]?.url;

    if (!imageUrl) return;

    const url = `https://lens.google.com/uploadbyurl?url=${encodeURIComponent(
      imageUrl,
    )}`;

    window.open(url, "_blank");
  };

  if (!equipment) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h6">Equipment not found.</Typography>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{ bgcolor: colors.primary[800] }}
        >
          <ArrowBackOutlined />
        </IconButton>
        <Typography
          variant="h5"
          fontWeight="bold"
          color={theme.palette.text.primary}
        >
          Gear Details
        </Typography>
      </Box>

      <GearHeader
        equipment={equipment}
        onImageSearch={handleImageSearch}
        onEdit={() => setEditOpen(true)}
      />

      <Box
        sx={{
          backgroundColor: colors.primary[900],
          borderRadius: "16px",
          p: 3,
          minHeight: 400,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, newVal) => setActiveTab(newVal)}
          sx={{
            mb: 3,
            minHeight: 36,
            "& .MuiTabs-indicator": {
              backgroundColor: theme.palette.text.primary,
              height: 3,
              borderRadius: "3px 3px 0 0",
            },
          }}
        >
          {[
            "Overview",
            "Allocations",
            "Maintance",
            "Documents",
            "Rentals",
            "Components",
            "Images",
          ].map((label, i) => (
            <Tab
              key={label}
              label={label}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                color:
                  activeTab === i
                    ? `${theme.palette.text.primary} !important`
                    : theme.palette.text.secondary,
                fontSize: "1rem",
                minWidth: "auto",
                px: 2,
                py: 1,
                minHeight: 36,
              }}
            />
          ))}
        </Tabs>

        <Box
          sx={{
            backgroundColor: colors.primary[800],
            borderRadius: "16px",
            p: 3,
            minHeight: 300,
          }}
        >
          {activeTab === 0 && <OverviewTab equipment={equipment} />}
          {activeTab === 1 && <AllocationsTab />}
          {activeTab === 2 && <MaintenanceTab />}
          {activeTab === 3 && <DocumentsTab />}
          {activeTab === 4 && <RentalsTab />}
          {activeTab === 5 && <ComponentsTab equipment={equipment} />}
          {activeTab === 6 && (
            <ImagesTab equipment={equipment} onImagesUpdated={fetchEquipment} />
          )}
        </Box>
      </Box>
      <AddEquipmentDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSubmit={handleUpdateEquipment}
        equipment={equipment}
        isEdit
      />
    </Box>
  );
}
